import React, { useEffect, useState, useMemo, useRef } from "react";
import { formatNumberWithCommas } from '../../../common/utility'
import moment from 'moment';
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Button,
    InputGroup
} from "reactstrap";
import Flatpickr from "react-flatpickr";

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";

import TableContainerV1 from "components/Common/TableContainerV1";
import { getTargetList } from "../../../helpers/UAM/report.helper"
import * as XLSX from 'xlsx';
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import 'flatpickr/dist/plugins/monthSelect/style.css';

const Target = () => {

    //meta title
    document.title = "Target List | DMS";

    const [targetData, setTargetData] = useState([])

    const [pagination, setPagination] = useState({ pageIndex: 0, pageCount: 0, totalRecords: 0 })
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchValue, setSearchValue] = useState("");
    const [columnId, setColumnId] = useState("id");
    const [sortingOrderStatus, setSortingOrderStatus] = useState("desc");
    const [filters, setFilters] = useState(null);
    const [sortingOptions, setSortingOptions] = useState({
        column: "",
        isAsc: false,
        optionalColumn: [],
    });
    const flatpickrRef = useRef(null);

    const user = JSON.parse(localStorage.getItem("user"));

    const columns = useMemo(
        () => [
            {
                Header: 'Dealer Name',
                accessor: 'entityName',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Target',
                accessor: 'target',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? formatNumberWithCommas(cellProps.value) : "";
                }
            },
            {
                Header: 'Month',
                accessor: 'month',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? formatDate(cellProps.value) : "";
                }
            },
            {
                Header: 'Zone',
                accessor: 'zone',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
        ],
        []
    );

    useEffect(() => {
        fetchCustomerWiseSalesReport({})
    }, [])

    const fetchCustomerWiseSalesReport = (data) => {
        getTargetList(data)
            .then((response) => {
                if (response.status && response.code == 200) {
                    setTargetData(response.data.data);
                    const { totalItems, totalPages, currentPage } = response.data
                    setPagination({
                        pageIndex: currentPage,
                        pageCount: totalPages,
                        totalRecords: totalItems,
                    })
                } else {
                    setPagination({ pageIndex: 0, pageCount: 0, totalRecords: 0 })
                    setTargetData([])
                }
            })
            .catch((err) => { console.log(err) })
    }

    const handlePageChange = (page, pageSize, searchKey = searchValue, columnName = columnId, sortingOrder = sortingOrderStatus, filter = filters) => {
        setPageSize(pageSize);
        setCurrentPage(page)
        fetchCustomerWiseSalesReport({ page, limit: pageSize, searchKey, columnName, sortingOrder, filters: filter })
    };

    const handleSortColumn = (option) => {
        setSortingOptions(option);
        const columnName = option.column;
        setColumnId(columnName);
        const sortingOrder = option.isAsc ? "asc" : "desc";
        setSortingOrderStatus(sortingOrder);
        handlePageChange(currentPage, pageSize, searchValue, columnName, sortingOrder);
    };

    const handleSearchFilter = (value, pageSize) => {
        const newValue = value !== undefined ? value : "";
        setSearchValue(newValue);
        handlePageChange(1, pageSize, newValue);
    };

    const handleClearSearch = () => {
        setSearchValue("");
        setFilters(null)
        if (flatpickrRef.current) {
            flatpickrRef.current.flatpickr.clear();
            flatpickrRef.current.flatpickr.close();
        }
        handlePageChange(1, pageSize, "", 'id', 'desc', null);
    };

    const handleDateFilter = () => {
        handlePageChange(1, pageSize, searchValue, columnId, sortingOrderStatus, filters)
    }

    const handleDateChange = (selectedDates) => {
        if (selectedDates.length === 2) {
            setFilters({
                startDate: selectedDates[0],
                endDate: selectedDates[1],
            })
        }
    };

    const downloadExcel = () => {
        let dataWithModifiedCol = targetData.map(report => {
            let newReport = {
                "Customer Name": report.entityName,
                "Target": formatNumberWithCommas(report.target),
                "Month": report.month,
                "Zone": report.zone,
            }
            return newReport
        })

        // Create a new workbook
        const workbook = XLSX.utils.book_new();
        const sheetData = XLSX.utils.json_to_sheet(dataWithModifiedCol);
        // Add the sheet to the workbook
        XLSX.utils.book_append_sheet(workbook, sheetData, 'Targets');
        // Save the workbook as an Excel file
        XLSX.writeFile(workbook, 'Targets.xlsx');
    };

    const customeFilterSection = (
        <div className="d-flex align-items-center justify-content-start">
            <InputGroup style={{ minWidth: '250px' }}>
                <Flatpickr
                    ref={flatpickrRef}
                    className="form-control"
                    placeholder="Start Date - End Date"
                    options={{
                        mode: 'range',
                        dateFormat: 'M-Y',
                        plugins: [
                            new monthSelectPlugin({
                              shorthand: true, 
                              dateFormat: "M-Y",
                              altFormat: "M Y",
                            })
                        ]
                    }}
                    value={filters?.startDate && filters?.endDate ? [filters?.startDate, filters?.endDate] : []}
                    onChange={handleDateChange}
                />
                <Button variant="primary"
                    onClick={handleDateFilter}
                    style={{ backgroundColor: "#344fa0", border: "none" }}>
                    Go
                </Button>
            </InputGroup>
        </div>
    )

    const buttonSection = (
        <div className="text-sm-end">
            {targetData?.length > 0 && (<Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={downloadExcel}
            >
                <i className="bx bx-archive-in me-1" />
                {'Download'}
            </Button>)}

        </div>
    );

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Reports" breadcrumbItem="Target List" />
                <Row>
                    <Col xs="12">
                        <Card>
                            <CardBody>
                                <TableContainerV1
                                    columns={columns}
                                    data={targetData}
                                    pagination={pagination}
                                    handlePageChange={handlePageChange}
                                    searchHelperText="Search by Customer Name"
                                    handleSearchFilter={handleSearchFilter}
                                    customFilterSection={customeFilterSection}
                                    customSection={buttonSection}
                                    sortOptions={sortingOptions}
                                    handleClearSearch={handleClearSearch}
                                    handleSortColumn={handleSortColumn}
                                    className="custom-header-css"
                                />
                            </CardBody>
                        </Card>

                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Target;
const formatDate = (dateString) => {
    return moment(dateString, "YYYY-MM").format("MMMM, YYYY");
};